import { Bell, ChevronLeft, Menu } from "lucide-react";
import { useTheme } from "@/shared/theme/ThemeProvider";
import logoLight from "@/shared/assets/elos-wordmark-light.svg";
import logoDark from "@/shared/assets/elos-wordmark-dark.svg";

interface HeaderBack {
  /** Rótulo da tela atual (mostrado no lugar da marca Elos). */
  title: string;
  /** Volta um nível na navegação do módulo. */
  onBack: () => void;
}

interface HeaderProps {
  onOpenMenu: () => void;
  onOpenNotif: () => void;
  /**
   * Quando presente, a tela NÃO é a raiz: o sino dá lugar à seta ‹ (voltar) e
   * o título da tela aparece no centro. Quando ausente (Home), mostra o layout
   * completo — sino à esquerda, Elos ao centro, hambúrguer à direita.
   */
  back?: HeaderBack;
  /** Acende a bolinha do sino (hoje: convites de ministério pendentes). */
  temNotificacoes?: boolean;
}

/**
 * Cabeçalho unificado (Model C). Na Home: sino de avisos à esquerda, marca
 * Elos ao centro/esquerda e hambúrguer à direita. Nas telas internas de um
 * módulo: seta ‹ de voltar à esquerda + título da tela, mantendo o hambúrguer
 * para pular entre módulos a qualquer momento.
 */
export function Header({ onOpenMenu, onOpenNotif, back, temNotificacoes = false }: HeaderProps) {
  const { theme } = useTheme();
  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-bg/80 px-4 py-3 backdrop-blur sm:px-6">
      {back ? (
        <>
          <button
            type="button"
            onClick={back.onBack}
            aria-label="Voltar"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-border bg-surface text-text transition-colors hover:border-primary hover:text-primary"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="min-w-0 flex-1 truncate text-center text-base font-extrabold tracking-tight text-text sm:text-left">
            {back.title}
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onOpenNotif}
            aria-label="Notificações"
            className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-border bg-surface text-text transition-colors hover:border-primary hover:text-primary"
          >
            <Bell size={20} />
            {temNotificacoes && (
              <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-danger ring-2 ring-surface" />
            )}
          </button>
          <div className="min-w-0 flex-1 text-center">
            {/* UX-10/reteste 23-24/07/2026: logo trocada de lockup (ícone + texto)
                para wordmark (texto com o "o" duplo formado pelos círculos
                entrelaçados) — mais alinhada à identidade da marca. h-8 (32px)
                ficava pequena entre os dois botões h-10 (40px); h-9 dá mais
                presença sem estourar a altura da barra. Sempre centralizada
                (antes ficava alinhada à esquerda a partir do breakpoint sm). */}
            <img
              src={theme === "dark" ? logoDark : logoLight}
              alt="Elos"
              className="inline-block h-9 w-auto"
            />
          </div>
        </>
      )}

      <button
        type="button"
        onClick={onOpenMenu}
        aria-label="Abrir menu da conta"
        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-border bg-surface text-text transition-colors hover:border-primary hover:text-primary"
      >
        <Menu size={20} />
      </button>
    </header>
  );
}
